import React from 'react';
import { Card, Typography, Row, Col, Spin, Alert } from 'antd';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useProductPerformance } from '../hooks';

const { Title, Text } = Typography;

// Color palette for different product types
const COLORS = ['#22D3EE', '#EF4444', '#F59E0B', '#8B5CF6', '#8884d8', '#82ca9d'];

interface ProductPerformanceTabProps {
  region?: number;
}

export const ProductPerformanceTab: React.FC<ProductPerformanceTabProps> = ({ region }) => {
  const { data, isLoading, error } = useProductPerformance(region);

  // Loading state
  if (isLoading) {
    return (
      <div className="flex justify-center items-center" style={{ minHeight: 400 }}>
        <Spin size="large" tip="Loading product performance data..." />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <Alert
        message="Error Loading Product Performance"
        description={error instanceof Error ? error.message : 'Failed to load product performance data'}
        type="error"
        showIcon
      />
    );
  }

  // Transform API data for pie chart
  const productMix = data?.data?.map((item, index) => ({
    name: item.productTypeLabel,
    value: item.count,
    color: COLORS[index % COLORS.length],
  })) || [];

  // These fields are not in the API response, so default to 0
  const foilQuotes = 0;
  const standardQuotes = 0;
  const foilPercentage = 0;
  const avgFrameCount = 0;
  const avgQuoteValue = 0;
  const totalRevenue = 0;

  return (
    <Row gutter={[24, 24]}>
      <Col xs={24} md={12}>
        <Card>
          <Title level={4}>Product Mix Breakdown</Title>
          <Text type="secondary" className="block mb-4">
            Distribution of quotes by foil type
          </Text>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie data={productMix} cx="50%" cy="50%" outerRadius={100} dataKey="value" label>
                {productMix.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </Col>

      <Col xs={24} md={12}>
        <Card>
          <Title level={4}>Product Performance Metrics</Title>
          <div className="mt-6 space-y-6">
            <div className="pb-4 border-b border-gray-200">
              <Text type="secondary">Foil vs Standard</Text>
              <Title level={3}>{foilQuotes} / {standardQuotes}</Title>
              <Text type="secondary" className="text-xs">
                {foilPercentage}% foil quotes
              </Text>
            </div>
            <div className="pb-4 border-b border-gray-200">
              <Text type="secondary">Avg Frame Count per Quote</Text>
              <Title level={3}>{avgFrameCount} frames</Title>
            </div>
            <div className="pb-4 border-b border-gray-200">
              <Text type="secondary">Avg Quote Value</Text>
              <Title level={3}>£{avgQuoteValue.toLocaleString()}</Title>
            </div>
            <div>
              <Text type="secondary">Total Revenue (Period)</Text>
              <Title level={3} className="text-green-500">
                £{totalRevenue.toLocaleString()}
              </Title>
            </div>
          </div>
        </Card>
      </Col>
    </Row>
  );
};
